import classNames from 'classnames';
import React, { FC, useCallback } from 'react';
import PaginationCell from './PaginationCell';
import { IconButton } from '../buttons';
import PaginationElipsisCell from './PaginationElipsisCell';
import { MAX_SHOW_ALL_THRESHOLD, shouldShowPage } from '@/utils/pagination';

interface Props {
  numberOfPages: number;
  onClick: (page: number) => void;
  activePage: number;
}

const Pagination: FC<Props> = ({ numberOfPages, onClick, activePage }) => {
  const pages = Array.from({ length: numberOfPages }, (_, i) => i + 1);

  const paginationCellPage = useCallback(
    (page: number) => <PaginationCell key={page} page={page} active={activePage === page} onClick={() => onClick(page)} />,
    [activePage, onClick],
  );

  return (
    <div className={classNames('pagination')}>
      <IconButton
        className="icon-button-v2 secondary"
        noBackground
        disabled={activePage === 1}
        onClick={() => onClick(activePage - 1)}
        icon="ds-chevron-left"
        data-testid="pagination-previous"
      />

      <div className="pagination-cells">
        {numberOfPages <= MAX_SHOW_ALL_THRESHOLD && pages.map(page => paginationCellPage(page))}

        {numberOfPages > MAX_SHOW_ALL_THRESHOLD && (
          <>
            {paginationCellPage(1)}
            {activePage > 4 && <PaginationElipsisCell data-testid="left-elipsis" />}

            {pages
              .filter(page => page !== 1 && page !== numberOfPages)
              .filter(page => shouldShowPage(page, numberOfPages, activePage))
              .map(page => paginationCellPage(page))}

            {activePage < numberOfPages - 3 && <PaginationElipsisCell data-testid="right-elipsis" />}
            {paginationCellPage(numberOfPages)}
          </>
        )}
      </div>

      <IconButton
        className="icon-button-v2 secondary"
        noBackground
        disabled={activePage === numberOfPages}
        onClick={() => onClick(activePage + 1)}
        icon="ds-chevron-right"
        data-testid="pagination-next"
      />
    </div>
  );
};

export default Pagination;
